import { Box, Flex, Text, Avatar, VStack } from "@chakra-ui/react";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { useMemo } from "react";
import { Conversation } from "../types/Conversation.type";
import { truncateHtml } from "helpers/stringHelper";
import { FiUser } from "react-icons/fi";
import Tooltip from "components/Tooltip";
import { getDateDisplay } from "../utils/getDateDisplay";
import UnreadMessage from "newui/components/UnreadMessage";

interface ConversationCardProps {
	active: boolean;
	conversation: Conversation;
	onClick: () => void;
}

/**
 * Provides the conversation display view
 * @param param0
 * @returns
 */
export const ConversationCard: React.FC<ConversationCardProps> = ({
	active,
	conversation,
	onClick,
}) => {
	// @ts-ignore
	const { isClientProfileActive } = useProfileContext();

	const updatedAt = useMemo(() => getDateDisplay(conversation.updated_at), [conversation.updated_at]);

	const trimmedSubject = truncateHtml(conversation.subject, {
		maxCharacters: 70,
		maxLines: 2,
	});

	const trimmedMessage = truncateHtml(conversation.latest_message_body, {
		maxCharacters: 50,
		maxLines: 2,
	});

	const hasUnreadMessages = conversation.unread_count > 0;

	return (
		<div className="relative">
			<Flex
				p={3}
				gap={3}
				cursor="pointer"
				position="relative" // <-- make card relative
				className={`border-b hover:bg-blue-50 ${active ? "bg-blue-200" : ""}`}
				onClick={onClick}
			>
				<div>
					{isClientProfileActive && conversation.created_by_company && (
						<>
							<Tooltip label={conversation.created_by_company.name}>
								{conversation.created_by_company.logo_path ? (
									<Avatar src={conversation.created_by_company.logo_path} size="md" />
								) : (
									<FiUser size={32} />
								)}
							</Tooltip>
						</>
					)}
					{hasUnreadMessages && <div className="absolute top-[-8px] left-3">
						<UnreadMessage size="sm" hideIcon value={conversation.unread_count} />
					</div>}
				</div>

				<Box flex="1">
					<Flex gap={1} justify="space-between">
						<Box minW={{ base: "200px", md: "250px" }}>
							<Tooltip label={trimmedSubject.wasTruncated ? conversation.subject : ''}>
								<Text pl={hasUnreadMessages && !isClientProfileActive ? 2 : 0} fontSize={"sm"} fontWeight="semibold">
									#{conversation.reference_id} {trimmedSubject.value}
								</Text>
							</Tooltip>
						</Box>

						<Text
							minW="70px"
							fontSize="xs"
							color="gray.500"
							textAlign="right"
							pt={1}
						>
							{updatedAt}
						</Text>
					</Flex>

					<Text
						fontSize="sm"
						color="gray.600"
						noOfLines={2}
						dangerouslySetInnerHTML={{ __html: trimmedMessage.value }}
						pl={hasUnreadMessages && !isClientProfileActive ? 2 : 0}
					></Text>
				</Box>
			</Flex>
		</div>
	);
};
